import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { PlayCircle, CheckCircle2, AlertTriangle, RotateCcw, Loader2, RefreshCw, Layers, Users } from "lucide-react";
import { conclaveApi } from "@/lib/conclaveApi";
import { apiErr, ADMIN_TOKEN_KEY } from "@/lib/authApi";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Conclave() {
  const { user, setUser } = useAdminSession();
  if (!user) return <AdminLogin onSuccess={setUser} />;
  return <ConclaveConsole />;
}

function ConclaveConsole() {
  const [eventId, setEventId] = useState(null);
  const [rules, setRules] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Fetch the first devadmin event via admin-visible endpoint — use ledger summary side-channel is overkill;
    // just pull one event from the public events endpoint by hitting our own db via a lightweight route.
    (async () => {
      try {
        const t = localStorage.getItem(ADMIN_TOKEN_KEY);
        // Any /admin endpoint returns event_id in its response; we use reconciliation summary to fetch it.
        const r = await axios.get(`${API}/admin/reconciliation/summary`, { params: { event_slug: "davangere-members-day-2026" }, headers: { Authorization: `Bearer ${t}` } });
        setEventId(r.data.event_id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  const loadAll = async () => {
    if (!eventId) return;
    setBusy(true);
    try {
      const [rl, rn] = await Promise.all([conclaveApi.rules(eventId), conclaveApi.runs(eventId)]);
      setRules(rl.rules); setRuns(rn.items || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { loadAll(); }, [eventId]); // eslint-disable-line

  // Auto-poll while any run is running
  useEffect(() => {
    if (!runs.some((r) => r.status === "running")) return;
    const iv = setInterval(loadAll, 2500);
    return () => clearInterval(iv);
  }, [runs]); // eslint-disable-line

  const generate = async () => {
    const seed = Math.floor(Math.random() * 100000);
    try {
      const r = await conclaveApi.generate(eventId, seed);
      toast.success(`Generation queued · run ${r.run_id.slice(0, 8)}`);
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow className="mt-4">Admin · Conclave</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Matching, rounds, seats.</h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-ghost" onClick={loadAll} disabled={busy} data-testid="conclave-refresh-btn"><RefreshCw size={14} /> Refresh</button>
            <button className="btn-red" onClick={generate} disabled={busy || !eventId} data-testid="conclave-generate-btn"><PlayCircle size={14} /> Generate schedule</button>
          </div>
        </div>

        {rules && (
          <Panel className="mt-6 p-5" data-testid="conclave-rules-summary">
            <MonoLabel>Current rules · from DevAdmin</MonoLabel>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <RuleChip k="Rounds" v={rules.rounds_count} />
              <RuleChip k="Table size" v={rules.table_size} />
              <RuleChip k="Max same-city" v={rules.max_same_city_per_table} />
              <RuleChip k="Max same-chapter" v={rules.max_same_chapter_per_table} />
              <RuleChip k="Category-fit weight" v={rules.category_fit_weight} />
              <RuleChip k="Same-cat override" v={rules.allow_same_category_when_forced ? "allow" : "never"} />
            </div>
          </Panel>
        )}

        <Panel className="mt-6 p-0 overflow-hidden" data-testid="conclave-runs-panel">
          <div className="p-5 border-b border-[var(--hairline)] flex items-center justify-between">
            <MonoLabel>Run history · {runs.length}</MonoLabel>
            <span className="mono-label" style={{ color: "var(--taupe)" }}>Only the most recently published run is active for /leader &amp; /member</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                <th className="p-3">Run</th><th className="p-3">Status</th><th className="p-3">Started</th><th className="p-3">Rounds</th><th className="p-3">Members</th><th className="p-3">Compromises</th><th className="p-3">Published</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-[var(--hairline)]" data-testid={`conclave-run-${r.id.slice(0, 8)}`}>
                  <td className="p-3 font-mono">{r.id.slice(0, 8)}</td>
                  <td className="p-3"><StatusChip status={r.status} /></td>
                  <td className="p-3 mono-label" style={{ color: "var(--taupe)" }}>{fmt(r.started_at)}</td>
                  <td className="p-3">{r.stats?.rounds_generated ?? "—"}</td>
                  <td className="p-3">{r.stats?.members_placed ?? "—"}</td>
                  <td className="p-3">{r.stats?.category_conflicts_relaxed ?? "—"}</td>
                  <td className="p-3">
                    {r.published_at ? <span style={{ color: "var(--green, #7ac47a)" }}>● live</span> : (
                      r.status === "completed" ? <DiffChip runId={r.id} /> : "—"
                    )}
                  </td>
                  <td className="p-3"><button className="btn-ghost" onClick={() => setSelectedRun(r.id)} data-testid={`conclave-run-open-${r.id.slice(0, 8)}`}>Open</button></td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={8} className="p-6 text-center mono-label" style={{ color: "var(--taupe)" }}>No runs yet — click Generate.</td></tr>}
            </tbody>
          </table>
        </Panel>

        {selectedRun && (
          <RunDetail runId={selectedRun} onClose={() => setSelectedRun(null)} onChanged={loadAll} eventId={eventId} />
        )}
      </div>
    </AdminShell>
  );
}

function RuleChip({ k, v }) {
  return (
    <div className="hairline p-3">
      <div className="mono-label" style={{ color: "var(--taupe)" }}>{k}</div>
      <div className="mt-1 font-mono">{String(v)}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const s = {
    running: { bg: "var(--panel)", fg: "var(--text)", icon: <Loader2 size={12} className="animate-spin" />, label: "running" },
    completed: { bg: "var(--panel)", fg: "var(--green, #7ac47a)", icon: <CheckCircle2 size={12} />, label: "completed" },
    failed: { bg: "var(--panel)", fg: "var(--red-2)", icon: <AlertTriangle size={12} />, label: "failed" },
  }[status] || { bg: "var(--panel)", fg: "var(--taupe)", icon: null, label: status };
  return <span className="chip inline-flex items-center gap-1.5" style={{ color: s.fg }}>{s.icon}{s.label}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { hour12: false }); } catch { return iso; }
}

function RunDetail({ runId, onClose, onChanged, eventId }) {
  const [run, setRun] = useState(null);
  const [round, setRound] = useState(1);
  const [tables, setTables] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { conclaveApi.run(runId).then(setRun).catch((e) => toast.error(apiErr(e))); }, [runId]);
  useEffect(() => {
    if (!run || run.status !== "completed" || !eventId) return;
    conclaveApi.tables(eventId, round).then((d) => setTables(d.tables || [])).catch((e) => toast.error(apiErr(e)));
  }, [run, round, eventId]);

  const publish = async () => {
    setBusy(true);
    try { await conclaveApi.publish(runId); toast.success("Schedule published · /leader and /member are live"); onChanged(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const rollback = async () => {
    const isPub = !!run?.published_at;
    if (isPub && !window.confirm("This run is PUBLISHED. Rolling it back will remove seat assignments from /leader and /member. Continue?")) return;
    if (!isPub && !window.confirm("Delete this run and all its assignments?")) return;
    setBusy(true);
    try {
      await conclaveApi.rollback(runId, isPub ? { force: true, reason: "admin rollback" } : {});
      toast.success("Run removed");
      onChanged(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!run) return null;
  const comps = run?.stats?.compromises || [];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-6" style={{ background: "rgba(0,0,0,.7)" }}
         data-testid="conclave-run-detail" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Panel className="p-6 w-full max-w-4xl mt-10">
        <div className="flex items-center justify-between">
          <div>
            <MonoLabel>Run · {run.id.slice(0, 8)}</MonoLabel>
            <h2 className="text-2xl font-serif mt-2 flex items-center gap-3">
              <StatusChip status={run.status} />
              {run.published_at && <span className="chip" style={{ color: "var(--green, #7ac47a)" }}>● published</span>}
            </h2>
          </div>
          <button className="btn-ghost" onClick={onClose} data-testid="conclave-run-close">Close</button>
        </div>

        {run.status === "failed" && (
          <div className="mt-4 hairline p-4" data-testid="conclave-run-error" style={{ color: "var(--red-2)" }}>
            <AlertTriangle size={14} className="inline-block mr-2" />{run.error}
          </div>
        )}

        {run.stats && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="conclave-run-stats">
            <Stat k="Rounds" v={run.stats.rounds_generated} />
            <Stat k="Tables/round" v={run.stats.tables_per_round} />
            <Stat k="Members placed" v={run.stats.members_placed} />
            <Stat k="Category conflicts relaxed" v={run.stats.category_conflicts_relaxed} />
            <Stat k="Repeat conflicts" v={run.stats.repeat_conflicts} />
            <Stat k="Avg category-fit" v={run.stats.avg_category_fit_score} />
            <Stat k="Elapsed" v={`${run.stats.seconds_taken}s`} />
            <Stat k="Seed" v={run.params?.seed} />
          </div>
        )}

        {comps.length > 0 && (
          <div className="mt-6" data-testid="conclave-compromises">
            <MonoLabel>Compromise explainer · {comps.length} rule-1 relaxations</MonoLabel>
            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
              These placements share a business category — review before publishing.
            </div>
            <div className="mt-3 max-h-52 overflow-y-auto hairline">
              <table className="w-full text-sm">
                <thead className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                  <tr><th className="p-2">Round</th><th className="p-2">Table</th><th className="p-2">Member</th><th className="p-2">Reason</th></tr>
                </thead>
                <tbody>
                  {comps.map((c, i) => (
                    <tr key={i} className="border-t border-[var(--hairline)]" data-testid={`compromise-${i}`}>
                      <td className="p-2">R{c.round}</td>
                      <td className="p-2">T{c.table_index + 1}</td>
                      <td className="p-2">{c.member_name || c.member_id.slice(0, 8)}</td>
                      <td className="p-2 mono-label" style={{ color: "var(--taupe)" }}>{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {run.status === "completed" && tables.length > 0 && (
          <div className="mt-6" data-testid="conclave-tables">
            <div className="flex items-center justify-between">
              <MonoLabel>Round drill-in</MonoLabel>
              <div className="inline-flex hairline">
                {Array.from({ length: run.stats?.rounds_generated || 0 }).map((_, i) => (
                  <button key={i} onClick={() => setRound(i + 1)}
                    data-testid={`conclave-round-${i + 1}`}
                    className={`px-3 py-1.5 text-xs mono-label ${round === i + 1 ? "bg-[var(--red)] text-[var(--text)]" : "text-[var(--taupe)]"}`}>
                    Round {i + 1}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
              {tables.map((t) => (
                <TableCard key={t.table_number} t={t} eventId={eventId} round={round} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center gap-3 justify-end">
          <button className="btn-ghost" onClick={rollback} disabled={busy} data-testid="conclave-rollback-btn">
            <RotateCcw size={14} /> {run.published_at ? "Force rollback" : "Delete run"}
          </button>
          {run.status === "completed" && !run.published_at && (
            <button className="btn-red" onClick={publish} disabled={busy} data-testid="conclave-publish-btn">
              <CheckCircle2 size={14} /> Publish this run
            </button>
          )}
        </div>
      </Panel>
    </div>
  );
}

function TableCard({ t, eventId, round }) {
  const [leader, setLeader] = useState(t.leader || null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(leader?.leader_name || "");
  const [phone, setPhone] = useState(leader?.leader_phone || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLeader(t.leader || null); setName(t.leader?.leader_name || ""); setPhone(t.leader?.leader_phone || ""); }, [t]);

  const save = async () => {
    if (!name && !phone) { toast.error("Enter a leader name or phone"); return; }
    setBusy(true);
    try {
      const d = await conclaveApi.assignLeader({
        event_id: eventId, round_number: round, table_number: t.table_number,
        leader_name: name || undefined, leader_phone: phone || undefined,
      });
      setLeader({ leader_name: d.leader_name, leader_phone: d.leader_phone, id: d.id, assigned_at: d.assigned_at });
      setEditing(false);
      toast.success(`Table ${t.table_number} · leader saved`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!leader?.id) { setEditing(false); return; }
    if (!window.confirm(`Remove leader from Table ${t.table_number}?`)) return;
    setBusy(true);
    try {
      await conclaveApi.unassignLeader(leader.id);
      setLeader(null); setName(""); setPhone(""); setEditing(false);
      toast.success("Leader removed");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="hairline p-3" data-testid={`conclave-table-${t.table_number}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono">Table {t.table_number}</div>
        <div className="mono-label" style={{ color: "var(--taupe)" }}>
          <Users size={12} className="inline-block mr-1" /> {t.members.length} · <Layers size={12} className="inline-block mx-1" /> {t.referrals_count} refs
        </div>
      </div>
      <div className="mt-2 text-xs" style={{ color: "var(--taupe)" }}>
        {t.members.map((m) => m.name).join(" · ")}
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--hairline)] flex items-center gap-2">
        <span className="mono-label" style={{ color: "var(--taupe)" }}>Leader:</span>
        {editing ? (
          <>
            <input data-testid={`leader-name-${t.table_number}`} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="input-x !py-1 !text-xs !w-32" />
            <input data-testid={`leader-phone-${t.table_number}`} placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} className="input-x !py-1 !text-xs !w-28 !font-mono" />
            <button className="btn-red !py-1 !px-2 text-xs" onClick={save} disabled={busy} data-testid={`leader-save-${t.table_number}`}>Save</button>
            <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => { setEditing(false); setName(leader?.leader_name || ""); setPhone(leader?.leader_phone || ""); }}>Cancel</button>
          </>
        ) : leader ? (
          <>
            <span className="text-xs" data-testid={`leader-assigned-${t.table_number}`}>{leader.leader_name || "—"} <span style={{ color: "var(--taupe)" }} className="mono-label ml-1">{leader.leader_phone || ""}</span></span>
            <button className="btn-ghost !py-1 !px-2 text-xs ml-auto" onClick={() => setEditing(true)} data-testid={`leader-edit-${t.table_number}`}>Edit</button>
            <button className="btn-ghost !py-1 !px-2 text-xs" onClick={clear} data-testid={`leader-clear-${t.table_number}`}>Clear</button>
          </>
        ) : (
          <button className="btn-ghost !py-1 !px-2 text-xs ml-auto" onClick={() => setEditing(true)} data-testid={`leader-add-${t.table_number}`}>+ Assign</button>
        )}
      </div>
    </div>
  );
}

function DiffChip({ runId }) {
  const [d, setD] = useState(null);
  useEffect(() => { conclaveApi.diff(runId).then(setD).catch(() => setD({ comparable: false, reason: "err" })); }, [runId]);
  if (!d) return <span className="mono-label" style={{ color: "var(--taupe)" }}>…</span>;
  if (!d.comparable) return <span className="chip" style={{ color: "var(--taupe)" }} title={d.reason}>fresh</span>;
  const total = d.changed_seats;
  if (total === 0) return <span className="chip" style={{ color: "var(--taupe)" }}>no change</span>;
  return (
    <span className="chip" data-testid={`conclave-run-diff-${runId.slice(0, 8)}`}
          title={`+${d.added} added, -${d.removed} removed, ${d.moved_members} moved`}
          style={{ color: "var(--red-2)", borderColor: "var(--red-2)" }}>
      ~{total} vs live
    </span>
  );
}

function Stat({ k, v }) {
  return (
    <div className="hairline p-3">
      <div className="mono-label" style={{ color: "var(--taupe)" }}>{k}</div>
      <div className="mt-1 font-mono">{v ?? "—"}</div>
    </div>
  );
}
